import React, { useState, useEffect, useRef } from "react";
import VideoFeed from "./VideoFeed";
import ThumbNavigator from "./ThumbNavigator";
import HelpPanel from "./HelpPanel";
import SingleVizControls from './SingleVizControls';
import "./index.css";

function App() {
  // --- STATE ---
  const [visualizations, setVisualizations] = useState([]);
  // Dedicated state for parameter mode to prevent race conditions
  const [singleViz, setSingleViz] = useState(null); 
  const [subjects, setSubjects] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHelpVisible, setHelpVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isParameterBased, setIsParameterBased] = useState(false);
  const appContainerRef = useRef(null);

  // --- DATA FETCHING ---
  useEffect(() => {
    const parts = window.location.pathname.split("/").filter(Boolean);
    const path = parts.length ? parts.join("/") : null;

    if (path) {
      // --- Parameter-Based Mode ---
      setIsParameterBased(true);
      setLoading(true);
      const visualName = path;
      const indexUrl = `https://storage.googleapis.com/visual-interactions/${visualName}/index.html`;
      const infoUrl = `https://storage.googleapis.com/visual-interactions/${visualName}/info.html`;

      fetch(indexUrl)
        .then(response => {
          if (!response.ok) throw new Error('Visualization not found.');
          return response.text();
        })
        .then(html => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, "text/html");
          const title = doc.querySelector('title')?.innerText || 'Untitled Visualization';
          const subject = doc.querySelector('meta[data-subject]')?.getAttribute('data-subject') || null;

          // Check if info.html exists robustly
          fetch(infoUrl, { method: 'HEAD' })
            .then(infoResponse => infoResponse.ok)
            .catch(() => false)
            .then(infoExists => {
              const vizObject = {
                url: indexUrl,
                name: title,
                subjectName: subject,
                infoUrl: infoExists ? infoUrl : null,
              };
              // Set the dedicated state for single-viz mode
              setSingleViz(vizObject);
              // Also set the visualizations array needed by VideoFeed
              setVisualizations([vizObject]); 
              setLoading(false);
            });
        })
        .catch(err => {
          setError(`Failed to load the visualization. ${err.message}`);
          setLoading(false);
        });
    } else {
      // --- Default Mode (from subjects.json) ---
      fetch("./subjects.json")
        .then((response) => response.json())
        .then((data) => {
          const flattenedVisualizations = data.flatMap((subject) =>
            subject.visualizations.map((viz) => ({ ...viz, subjectName: subject.name }))
          );
          setVisualizations(flattenedVisualizations);
          setSubjects(data);
          setLoading(false);
        })
        .catch((err) => {
          setError(err.message);
          setLoading(false);
        });
    }
  }, []);

  // --- Scroll and interaction handlers ---
  const triggerScroll = () => {
    window.requestAnimationFrame(() => {
      const cur = window.scrollY || document.documentElement.scrollTop || 0;
      if (cur < 1) { window.scrollTo(0, 1); }
    });
  };

  useEffect(() => {
    if (!loading) { setTimeout(triggerScroll, 200); }
  }, [loading]);

  useEffect(() => {
    const handleResize = () => triggerScroll();
    window.addEventListener("orientationchange", handleResize);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("orientationchange", handleResize);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const changeSlide = (newIndex) => {
    setHelpVisible(false);
    setCurrentIndex(newIndex);
    triggerScroll();
  };

  const handleNext = () => {
    if (visualizations.length === 0) return;
    const newIndex = (currentIndex + 1) % visualizations.length;
    changeSlide(newIndex);
  };

  const handlePrev = () => {
    if (visualizations.length === 0) return;
    const newIndex = (currentIndex === 0 ? visualizations.length - 1 : currentIndex - 1);
    changeSlide(newIndex);
  };

  const handleSubjectChange = (direction) => {
    if (visualizations.length === 0 || subjects.length < 2) return;
    const currentViz = visualizations[currentIndex];
    const currentSubjectIndex = subjects.findIndex((s) => s.name === currentViz.subjectName);
    let nextSubjectIndex = (direction === 'next')
      ? (currentSubjectIndex + 1) % subjects.length
      : (currentSubjectIndex - 1 + subjects.length) % subjects.length;
    const nextSubject = subjects[nextSubjectIndex];
    const firstVizOfNextSubjectIndex = visualizations.findIndex((viz) => viz.subjectName === nextSubject.name);
    if (firstVizOfNextSubjectIndex !== -1) {
      changeSlide(firstVizOfNextSubjectIndex);
    }
  };
  
  const handleHomeClick = () => {
    window.location.href = '/';
  };

  // --- RENDER LOGIC ---

  // Create a single, reliable source of truth for the currently displayed viz
  const currentViz = isParameterBased ? singleViz : visualizations[currentIndex];

  if (loading) return <div className="app-status">Loading Visualizations...</div>;
  if (error) return <div className="app-status">Error: {error}</div>;

  return (
    <div className="app-container" ref={appContainerRef}>
      <VideoFeed visualizations={visualizations} currentIndex={currentIndex} />
      
      {isParameterBased ? (
        // In parameter mode, render controls if singleViz state is populated
        singleViz && (
          <SingleVizControls
            onHomeClick={handleHomeClick}
            onInfoClick={() => setHelpVisible(!isHelpVisible)}
            onNewTabClick={() => window.open(singleViz.url, '_blank')}
            infoUrl={singleViz.infoUrl}
          />
        )
      ) : (
        // In default mode, render thumb navigator
        visualizations.length > 1 && (
          <ThumbNavigator
            onNext={handleNext}
            onPrev={handlePrev}
            onNextSubject={() => handleSubjectChange('next')}
            onPrevSubject={() => handleSubjectChange('prev')}
            infoUrl={currentViz?.infoUrl}
            vizUrl={currentViz?.url}
            onInfoClick={() => setHelpVisible(!isHelpVisible)}
            onNewTabClick={() => window.open(currentViz?.url, '_blank')}
          />
        )
      )}

      {/* This panel now reliably uses currentViz for both modes */}
      {currentViz?.infoUrl && (
        <HelpPanel
          isOpen={isHelpVisible}
          onClose={() => setHelpVisible(false)}
          infoUrl={currentViz.infoUrl}
        />
      )}
    </div>
  );
}

export default App;